import { PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';

import { Div, StyledUtilsProps } from '../helpers/StyledUtils';
import Button from './Button';
import Icon, { IconName } from './Icon';
import Text from './Text';

const icons_for_type = {
  warning: 'warning',
  success: 'success',
  danger: 'error',
  info: 'info',
  neutral: 'info',
};

interface Props extends StyledUtilsProps {
  title?: string;
  inline?: boolean;

  warning?: boolean;
  success?: boolean;
  danger?: boolean;
  info?: boolean;
  neutral?: boolean;

  buttonPrimary?: boolean;

  no_border?: boolean;

  action?: {
    label: string;
    onClick?: () => void;
    to?: string;
    icon?: IconName;
  };
}

const StyledAlert = styled(Div)<Props>(
  ({ theme, ...props }) => css`
    font-weight: ${theme.font_weights.medium};
    box-sizing: border-box;
    padding: ${theme.spacing(2)} ${theme.spacing(4)};
    text-align: left;
    ${!props.inline
      ? css`
          display: flex;
          align-items: center;
          justify-content: center;
        `
      : css`
          display: flex;
          align-items: flex-start;
          padding: ${theme.spacing(3)};
          border-radius: ${theme.radius.normal};
        `}

    ${theme.optionsToCss(
      {
        neutral: css`
          background-color: ${theme.colors.surface.base.variant_surface_3};
          border: 1px solid ${theme.colors.outline.neutral};
        `,
        info: css`
          background-color: ${theme.colors.surface.container.primary};
          border: 1px solid ${theme.colors.outline.primary};
        `,
        success: css`
          background-color: ${theme.colors.surface.container.success};
          border: 1px solid ${theme.colors.outline.success};
        `,
        warning: css`
          background-color: ${theme.colors.surface.container.warning};
          border: 1px solid ${theme.colors.outline.warning};
        `,
        danger: css`
          background-color: ${theme.colors.surface.container.danger};
          border: 1px solid ${theme.colors.outline.danger};
        `,
      },
      props,
      'neutral',
    )}

    ${props.no_border &&
    css`
      border: none;
    `}
  `,
);

const Alert: React.FC<PropsWithChildren<Props>> = ({ children, inline, action, ...props }) => (
  <StyledAlert inline={inline} {...props}>
    <Div h={props.title ? { px: 20 } : { px: 28 }} flex={{ align: 'center' }}>
      <Icon
        icon={icons_for_type[Object.keys(icons_for_type).find((key) => !!props[key]) || 'neutral']}
        warning={!!props.warning}
        success={!!props.success}
        danger={!!props.danger}
        primary={!!props.info}
        left
      />
    </Div>
    {props.title ? (
      <Div>
        <Text semi as="p" m={{ b: 1 }}>
          {props.title}
        </Text>
        <Text as="p" m={0}>
          {children}
        </Text>
      </Div>
    ) : (
      <Text as="p" m={0} p={{ y: 1 }}>
        {children}
      </Text>
    )}
    {action && (
      <Div m={{ l: 'auto' }} p={{ l: 8 }}>
        <Button
          primary={!!props.info || !!props.buttonPrimary}
          outline
          onClick={action.onClick}
          to={action.to}
          icon={action.icon}>
          {action.label}
        </Button>
      </Div>
    )}
  </StyledAlert>
);

export default Alert;
